import React, {useMemo, useState} from "react";
import {Button, Modal} from "antd";
import useUserContext from "../../../hooks/useUserContext";
import useToast from "../../../hooks/useToast";
import {BlackoutDateProps, NewBlackoutDateProps} from "../../../types/BlackoutPeriod";
import {blackoutPeriodFormValidator} from "./BlackoutPeriodFormValidator";
import "./blackout-period-modals.css";
import {ToastSkin} from "../../../context/ToastContext";
import {createBlackoutPeriod} from "../../../api/createBlackoutPeriod";
import useAppContext from "../../../hooks/useAppContext";
import {checkTimeDifference} from "./checkTimeDifference";
import NewBlackoutPeriodForm from "../../forms/NewBlackoutPeriodForm";
import {updateBlackoutPeriod} from "../../../api/updateBlackoutPeriod";
import dayjs from "dayjs";
import {deleteBlackoutPeriod} from "../../../api/deleteBlackoutPeriod";

interface NewBlackoutPeriodModalProps {
    isOpen: boolean;
    onClose: () => void;
    blackoutPeriodRecordId: number | null;
}

export const DEFAULT_FORM_VALUES: NewBlackoutDateProps = {
    title: '',
    fromDay: undefined,
    fromDate: null,
    toDay: undefined,
    toDate: null,
    fromDayTime: undefined,
    toDayTime: undefined,
    fromDateTime: undefined,
    toDateTime: undefined,
}

const NewBlackoutPeriodModal = ({
    isOpen,
    onClose,
    blackoutPeriodRecordId,
}: NewBlackoutPeriodModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { selectedDomain } = useUserContext();
    const { showToast, closeToast } = useToast();
    const [selectedPeriodType, setSelectedPeriodType] = useState<1 | 2>(1);
    const { actions: { loadBlackoutPeriods }, data: { blackoutPeriods } } = useAppContext();

    const editedBlackoutPeriod = useMemo(() => {
        const edited = blackoutPeriods.data.find((bop: BlackoutDateProps) => bop.id === blackoutPeriodRecordId);
        if(edited) {
            edited.fromDate && edited.toDate ? setSelectedPeriodType(2) : setSelectedPeriodType(1);
            return {...edited, fromDayTime: edited.fromTime, toDayTime: edited.toTime, fromDateTime: edited.fromTime, toDateTime: edited.toTime, fromDate: edited.fromDate ? dayjs(edited.fromDate) : null, toDate: edited.toDate ? dayjs(edited.toDate) : null};
        } else {
            return null;
        }
    }, [blackoutPeriodRecordId, blackoutPeriods.data]);

    const onCloseModal = () => {
        onClose();
    }

    const onCreateNewBlackoutPeriod = async (formValues: NewBlackoutDateProps) => {
        if(!selectedDomain) return;
        setIsLoading(true);
        try {
            const body:BlackoutDateProps = selectedPeriodType === 1
                ? {...formValues, fromTime: formValues.fromDayTime!, toTime: formValues.toDayTime!, fromDate: undefined, toDate: undefined }
                : {...formValues, fromTime: formValues.fromDateTime!, toTime: formValues.toDateTime!, fromDay: undefined, toDay: undefined, fromDate: formValues.fromDate?.toDate(), toDate: formValues.toDate?.toDate() };
            const response = editedBlackoutPeriod && editedBlackoutPeriod.id ? await updateBlackoutPeriod(selectedDomain, editedBlackoutPeriod.id, {...body, id: editedBlackoutPeriod.id} ) : await createBlackoutPeriod(selectedDomain, body);
            if(response.status === 200) {
                setIsLoading(false);
                showToast({skin: ToastSkin.approval, text: 'The Blackout Period was successfully created'});
                loadBlackoutPeriods();
                onCloseModal();
            } else {
                showToast({text: 'An unknown error occurred while saving your Blackout Period record'});
            }
        } catch (error) {
            setIsLoading(false);
            console.log("error: ", error);
            showToast({ text: 'An unknown error occurred while saving your Blackout Period record'});
        }
    }

    const onDelete = async () => {
        const response = editedBlackoutPeriod?.id && selectedDomain && await deleteBlackoutPeriod(selectedDomain, editedBlackoutPeriod.id);
        if(response.data === 'ok') {
            showToast({ skin: ToastSkin.approval, text: 'The Blackout Period was successfully deleted' });
            loadBlackoutPeriods();
            onCloseModal();
        }
    }

    const onDeleteRequest = () => {
        showToast({ skin: ToastSkin.danger, text: 'This action will be irreversible, Are you sure you want to delete this Blackout Period?', buttons: [
                { text: 'Yes', onClick: onDelete },
                { text: 'No, Cancel', onClick: closeToast }
            ] });
    }

    const onSubmit = async (formValues: NewBlackoutDateProps) => {
        const errors = blackoutPeriodFormValidator(formValues, selectedPeriodType);
        if(!errors.length) {
            const blackedOutHours = checkTimeDifference(selectedPeriodType, formValues);
            console.log("hour difference: ", blackedOutHours);
            if(blackedOutHours > 48) {
                const blackedOutDays = Math.floor(blackedOutHours / 24);
                showToast({
                    skin: ToastSkin.danger,
                    text: `You are about to set over ${blackedOutDays} days of complete blackout, are you sure about it?`,
                    displayTime: 15000,
                    buttons: [
                        { text: 'Yes', onClick: () => {
                            onCreateNewBlackoutPeriod(formValues);
                            closeToast();
                        } },
                        { text: 'No, Cancel', onClick: closeToast }
                    ]});
            } else {
                onCreateNewBlackoutPeriod(formValues);
            }
        } else {
            showToast({ text: errors?.length ? errors[0] : 'An unknown error occurred while saving your Blackout Period record'});
        }
    }

    return (
        <Modal
            open={isOpen}
            onCancel={onCloseModal}
            confirmLoading={isLoading}
            okButtonProps={{ style: { display: 'none' }}}
            cancelButtonProps={{ style: { display: 'none' }}}
            width={700}
            destroyOnHidden={true}
            title="Create New Blackout Period"
        >
            <NewBlackoutPeriodForm onSubmit={onSubmit} onDelete={onDeleteRequest} onClose={onCloseModal} editedBlackoutPeriod={editedBlackoutPeriod} selectedPeriodType={selectedPeriodType} setSelectedPeriodType={setSelectedPeriodType} />
        </Modal>
    );
}

export default NewBlackoutPeriodModal;